import type { ISceneLoaderAsyncResult } from '@babylonjs/core/Loading/sceneLoader';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Observable, type Observer } from '@babylonjs/core/Misc/observable';
import { AssetContainer } from '@babylonjs/core/assetContainer';
import type { Node } from '@babylonjs/core/node';
import type { Scene as BabylonScene } from '@babylonjs/core/scene';
import type { Nullable } from '@babylonjs/core/types';
import { ResourceContext } from '@seddi/core';
import type { IScene, ISceneEntity } from '@seddi/scene';
import { FabricLoader } from '../resources/fabricLoader';
import { SceneResourcesContext } from '../resources/sceneResourcesContext';
import * as Scene from '../scene/scene';
import type { EntityRenderable } from '../renderables/entityRenderable';
import { EntityLoader } from './entityLoader';

/**
 * Loads a scene.
 */
export class SceneLoader extends SceneResourcesContext {
  /** Entities context. */
  public readonly entities: ResourceContext<EntityRenderable>;

  /** Entity loader. */
  private readonly entityLoader: EntityLoader;

  constructor(scene: BabylonScene) {
    super(scene);

    this.entities = new ResourceContext<EntityRenderable>();

    this.entityLoader = new EntityLoader(
      this.scene,
      this.fileLoader,
      this.surfaceMaterialLoader,
      this.buttons,
      this.fabrics,
      this.logos,
    );
  }

  /** @internal */
  public _assetContainer: Nullable<AssetContainer> = null;

  private root: Nullable<TransformNode> = null;

  /**
   * Observable raised when the asset is completely loaded, immediately before the loader is disposed.
   * For assets with LODs, raised when all of the LODs are complete.
   * For assets without LODs, raised when the model is complete, immediately after the loader resolves the returned promise.
   */
  public readonly onCompleteObservable = new Observable<void>();

  private _onCompleteObserver!: Nullable<Observer<void>>;

  /**
   * Callback raised when the asset is completely loaded, immediately before the loader is disposed.
   * For assets with LODs, raised when all of the LODs are complete.
   * For assets without LODs, raised when the model is complete, immediately after the loader resolves the returned promise.
   */
  public set onComplete(callback: () => void) {
    if (this._onCompleteObserver) {
      this.onCompleteObservable.remove(this._onCompleteObserver);
    }
    this._onCompleteObserver = this.onCompleteObservable.add(callback);
  }

  /**
   * Loads a scene.
   * @param data - the scene data to load.
   * @param context - Scene resource context.
   */
  loadAsync(data: IScene): Promise<void> {
    return this._loadAsync(data);
  }

  /**
   * Load into an asset container.
   * @param scene - The scene to load into
   * @param data - The data to import
   * @returns The loaded asset container
   */
  async loadAssetContainerAsync(data: IScene): Promise<AssetContainer> {
    this._assetContainer = new AssetContainer(this.scene);

    await this._loadAsync(data);

    const result: ISceneLoaderAsyncResult = {
      meshes: [],
      particleSystems: [],
      skeletons: [],
      animationGroups: [],
      transformNodes: [],
      geometries: [],
      lights: [],
    };

    // Fill asset container
    Array.prototype.push.apply(this._assetContainer.geometries, result.geometries);
    Array.prototype.push.apply(this._assetContainer.meshes, result.meshes);
    Array.prototype.push.apply(this._assetContainer.particleSystems, result.particleSystems);
    Array.prototype.push.apply(this._assetContainer.skeletons, result.skeletons);
    Array.prototype.push.apply(this._assetContainer.animationGroups, result.animationGroups);
    // Array.prototype.push.apply(this._assetContainer.materials, materials);
    // Array.prototype.push.apply(this._assetContainer.textures, textures);
    Array.prototype.push.apply(this._assetContainer.lights, result.lights);
    Array.prototype.push.apply(this._assetContainer.transformNodes, result.transformNodes);
    // Array.prototype.push.apply(this._assetContainer.cameras, cameras);
    // Array.prototype.push.apply(this._assetContainer.morphTargetManagers, morphTargetManagers);
    return this._assetContainer;
  }

  /**
   * Loads an entity.
   * If the entity is already loaded, returns a resolved promise.
   * If the entity is currently being loaded, returns a promise that will be resolved when the entity finishes loading.
   * @param entity - Entity to load.
   * @param scene - Scene to which the entity belongs.
   */
  async loadEntity(scene: IScene, entity: ISceneEntity): Promise<void> {
    // Load dependencies first.
    const dependencies = this.findEntityDependencies(scene, entity);

    if (dependencies.length) {
      const promises = dependencies.map(e => this.loadEntity(scene, e));
      await Promise.all(promises);
    }

    // Load current.
    return this.entities.load(
      entity._id,
      () => this.entityLoader.loadAssetContainerAsync(scene, entity, this._assetContainer),
      entity => this.entityLoader.unload(entity),
    );
  }

  /**
   * Finds peer dependencies.
   * @param scene - Scene to which the entity belongs.
   * @param entity - Entity to load.
   */
  findEntityDependencies(scene: IScene, entity: ISceneEntity): ISceneEntity[] {
    const hasGeometry = Scene.hasGeometrySurface(scene);
    const set = new Set<string>();
    const {
      components: { garmentPiece, garmentSeam },
    } = entity;

    // Pieces and seams depend on garment.
    if (hasGeometry && (garmentPiece || garmentSeam)) {
      set.add(entity.parent!);
    }

    // Seams depend on pieces.
    if (garmentSeam) {
      const segments = garmentSeam.lines.map(line => line.segments).flat();
      for (const segment of segments) {
        set.add(segment.piece);
      }
    }

    const { children } = scene;
    const arr = Array.from(set);
    const ret = arr.map(id => children.find(e => e._id === id)!);
    return ret;
  }

  /**
   * Unloads a scene.
   * @param scene - the scene data to load.
   * @param context - Scene resource context.
   */
  async unload(scene: IScene): Promise<void> {
    const { children } = scene;

    // unload entities
    const children_ = children.filter(e => e.parent && !e.components.garmentFold);
    const promises = children_.map(e => this.unloadEntity(e));
    await Promise.all(promises);
  }

  /**
   * Unloads an entity.
   * @param entity - Entity to unload.
   * @param scene - Scene to which the entity belongs.
   * @param sceneContext - Scene resource sceneC
   */
  async unloadEntity(entity: ISceneEntity): Promise<void> {
    // TODO: dependencies
    // unload current
    return this.entities.unload(entity._id);
  }

  /**
   * Create root node.
   * @param id - Root node name.
   */
  private createRoot(id: string) {
    this.scene._blockEntityCollection = Boolean(this._assetContainer);
    this.root = new TransformNode(id, this.scene);
    this.root._parentContainer = this._assetContainer;
    this.scene._blockEntityCollection = false;
    this.root.setEnabled(false);
  }

  /**
   * Loads a scene.
   * @param data - the scene data to load.
   */
  private async _loadAsync(data_: IScene) {
    // Sanitize data.
    const data = this.sanitizeData(data_);

    // Load resources in parallel.
    const resources = [];

    // Load buttons.
    for (const button of data.buttons) {
      resources.push(this.buttons.loadEx(button));
    }

    // Load fabrics.
    const pieces = data.children.filter(e => Boolean(e.components.garmentPiece));
    const fabricSet = new Set<string>();
    for (const piece of pieces) {
      const { fabric: fabricId, fabricColor } = piece.components.garmentPiece!;
      const uniqueId = FabricLoader.GetFabricId(fabricId, fabricColor);
      if (!fabricSet.has(uniqueId)) {
        const fabric = data.fabrics.find(f => f.id === fabricId)!;
        resources.push(this.fabrics.loadEx(fabric, fabricColor));
      }
    }

    // Load logos.
    for (const logo of data.logos) {
      resources.push(this.logos.loadEx(logo));
    }

    // Load garment geometry surfaces.
    const hasGeometry = Scene.hasGeometrySurface(data);
    if (hasGeometry) {
      const garments = data.children.filter(e => Boolean(e.components.garment));
      for (const garment of garments) {
        resources.push(this.loadEntity(data, garment));
      }
    }

    await Promise.all(resources);

    // load entities
    this.createRoot(data._id);
    const childrenPromises = data.children.map(entity => this.loadEntity(data, entity));
    await Promise.all(childrenPromises);

    // build hierarchy
    const nodes = new Map<string, Node>();
    for (const entity of data.children) {
      const renderable = this.entities.get(entity._id)!;
      nodes.set(entity._id, renderable.node);
    }

    for (const entity of data.children) {
      if (!entity.parent) {
        continue;
      }
      const parent = nodes.get(entity.parent);
      if (!parent) {
        continue;
      }
      const node = nodes.get(entity._id)!;
      node.parent = parent;
    }
  }

  /**
   * Transforms input.
   * @param data - the scene data to load.
   */
  private sanitizeData(data: IScene): IScene {
    // Remove unnecessary entities.
    const children = data.children.filter(e => e.parent && !e.components.garmentFold);

    // Remove unnecessary fabrics.
    const pieces = children.filter(e => Boolean(e.components.garmentPiece));
    const fabrics = data.fabrics.filter(f => {
      const piece = pieces.find(e => e.components.garmentPiece!.fabric === f.id);
      return Boolean(piece);
    });

    return {
      ...data,
      children,
      fabrics,
    };
  }
}
